import { useState } from "react";
import { Plug, Loader2, ExternalLink, KeyRound, IdCard, Phone } from "lucide-react";
import { toast } from "react-toastify";
import { conectarConta } from "../services/whatsappOficialApi";

const TOKEN_REGEX = /^EAA[A-Za-z0-9]+$/;
const ID_REGEX = /^\d{1,32}$/;

const ERROR_MESSAGES = {
  INVALID_TOKEN_FORMAT: "Token inválido. O System User Token deve começar com EAA.",
  INVALID_WABA_ID: "WABA ID precisa ser numérico.",
  INVALID_PHONE_NUMBER_ID: "Phone Number ID precisa ser numérico.",
  TOKEN_INVALID: "Token rejeitado pela Meta. Verifique se ele tem as permissões whatsapp_business_management e whatsapp_business_messaging.",
  PHONE_NUMBER_ID_NOT_FOUND: "Phone Number ID ou WABA ID não encontrado na Meta. Confira os valores no painel.",
  WABA_PHONE_MISMATCH: "Esse Phone Number ID não pertence à WABA informada.",
  WEBHOOK_SUBSCRIBE_FAILED: "Não foi possível conectar o webhook ao app. Verifique se o app tem permissão sobre essa WABA.",
  RATE_LIMIT_EXCEEDED: "Muitas tentativas. Aguarde alguns minutos e tente novamente.",
  META_UNAVAILABLE: "Meta está instável agora. Tente novamente em instantes.",
  DB_ERROR: "Erro ao gravar no banco. Tente novamente.",
  UNAUTHORIZED: "Sessão expirou. Faça login de novo.",
};

export default function ConnectForm({ onSuccess }) {
  const [token, setToken] = useState("");
  const [waba, setWaba] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorBody, setErrorBody] = useState(null);

  function clientValidate() {
    if (!TOKEN_REGEX.test(token.trim())) return "INVALID_TOKEN_FORMAT";
    if (!ID_REGEX.test(waba.trim())) return "INVALID_WABA_ID";
    if (!ID_REGEX.test(phone.trim())) return "INVALID_PHONE_NUMBER_ID";
    return null;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setErrorBody(null);
    const localErr = clientValidate();
    if (localErr) {
      setErrorBody({ error: localErr });
      return;
    }
    setLoading(true);
    try {
      const conta = await conectarConta({
        system_user_token: token.trim(),
        waba_id: waba.trim(),
        phone_number_id: phone.trim(),
      });
      toast.success(`Conectado: ${conta.display_phone_number} (${conta.templates_count} templates)`);
      onSuccess?.(conta);
    } catch (err) {
      const body = err.response?.data;
      setErrorBody(body || { error: "UNKNOWN", message: err.message });
    } finally {
      setLoading(false);
    }
  }

  const errorMsg = errorBody
    ? ERROR_MESSAGES[errorBody.error] || errorBody.message || "Erro desconhecido"
    : null;

  return (
    <form onSubmit={handleSubmit} className="bg-surface-1 border border-white/[0.06] rounded-lg p-6 max-w-2xl">
      <h2 className="text-xl font-semibold text-white mb-1">Conectar WhatsApp Oficial</h2>
      <p className="text-sm text-ink-3 mb-6">
        Cole o System User Token gerado no Business Manager e os IDs da sua WABA.
      </p>

      <div className="bg-base border border-white/[0.06] rounded p-4 mb-6 text-sm text-ink-2 space-y-2">
        <p className="font-medium text-ink-1">Onde encontrar esses valores?</p>
        <p>
          <strong className="text-brand-400">1.</strong> System User Token —{" "}
          <a
            href="https://business.facebook.com/settings/system-users"
            target="_blank"
            rel="noreferrer"
            className="underline inline-flex items-center gap-1 text-brand-300"
          >
            business.facebook.com → Configurações → Usuários do Sistema <ExternalLink className="w-3 h-3" />
          </a>
          {" "}→ "Gerar novo token" → escolha o app, marque{" "}
          <code className="bg-surface-2 px-1 rounded text-xs">whatsapp_business_management</code>{" "}+{" "}
          <code className="bg-surface-2 px-1 rounded text-xs">whatsapp_business_messaging</code>{" "}
          → expiração <strong>Nunca</strong>.
        </p>
        <p>
          <strong className="text-brand-400">2.</strong> WABA ID e Phone Number ID —{" "}
          <a
            href="https://developers.facebook.com/apps/"
            target="_blank"
            rel="noreferrer"
            className="underline inline-flex items-center gap-1 text-brand-300"
          >
            developers.facebook.com → seu app → WhatsApp → API Setup <ExternalLink className="w-3 h-3" />
          </a>
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="flex items-center gap-2 text-sm text-ink-2 mb-1">
            <KeyRound className="w-4 h-4" /> System User Token
          </label>
          <textarea
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="EAA..."
            rows={3}
            className="w-full bg-base border border-white/[0.08] rounded px-3 py-2 text-white text-sm font-mono focus:outline-none focus:border-brand-500"
            spellCheck={false}
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="flex items-center gap-2 text-sm text-ink-2 mb-1">
              <IdCard className="w-4 h-4" /> WABA ID
            </label>
            <input
              type="text"
              value={waba}
              onChange={(e) => setWaba(e.target.value)}
              placeholder="472487289283328"
              className="w-full bg-base border border-white/[0.08] rounded px-3 py-2 text-white text-sm font-mono focus:outline-none focus:border-brand-500"
              required
            />
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm text-ink-2 mb-1">
              <Phone className="w-4 h-4" /> Phone Number ID
            </label>
            <input
              type="text"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="473191925877020"
              className="w-full bg-base border border-white/[0.08] rounded px-3 py-2 text-white text-sm font-mono focus:outline-none focus:border-brand-500"
              required
            />
          </div>
        </div>
      </div>

      {errorMsg && (
        <div className="mt-4 bg-red-950 border border-red-800 text-red-200 rounded p-3 text-sm">
          {errorMsg}
        </div>
      )}

      <button
        type="submit"
        disabled={loading}
        className="mt-6 inline-flex items-center gap-2 bg-brand-600 hover:bg-brand-700 disabled:bg-surface-3 disabled:cursor-not-allowed text-white px-4 py-2 rounded font-medium transition"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plug className="w-4 h-4" />}
        {loading ? "Conectando..." : "Conectar"}
      </button>
    </form>
  );
}
